import './App.css';

import React from 'react';
import { BrowserRouter, Redirect, Route, useLocation } from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';

import DateFnsUtils from '@date-io/date-fns';
import { CssBaseline } from '@material-ui/core';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { api as muiApi } from '@sierralabs/react-material-ui';
import { api as platformStackApi } from '@sierralabs/core-ui';
import {
  defaultSiteEventPlugins,
  defaultSitePlugins,
  SiteProvider,
  SiteRootRoutes,
  SiteRoutePermission
} from '@sierralabs/site-ui';

import AppThemeProvider, { theme } from './AppThemeProvider';
import api from './common/api/api';
import { useRemoveJiraWidget } from './common/useRemoveJiraWidget';
import CheckVersion from './common/version/CheckVersion';
import ProtectedRoute from './common/ProtectedRoute';
import { navigationList } from './environments/navigation';
import environment, { env, environmentV2 } from './environments';
import GuestGuard from './guards/guest.guard';
import AuthGuard from './guards/auth.guard';
import SignupGuard from './guards/signup.guard';
import Login from './login/Login';
import { LoginProvider } from './login/Login.context';
import PasswordRecover from './login/PasswordRecover';
import PasswordReset from './login/PasswordReset';
import { defaultQmsSitePlugins } from './plugins/default';
import { defaultQmsSiteEventPlugins } from './plugins/events/default';
import { defaultQmsFormElementPlugins } from './plugins/form-elements/default';
import EntityProvider from './secure-portal/entity/Entity.context';
import WorkflowProvider from './secure-portal/workflow/Workflow.context';
import { FileExplorerProvider } from './secure-portal/documents/components/FileExplorer.context';
import { useJiraAcRoute } from './secure-portal/jira-connect/JiraConnect.hooks';
import { JiraConnectProvider } from './secure-portal/jira-connect/JiraConnect.context';
import { JiraServerProvider } from './secure-portal/jira-server/jira-server.context';
import { SessionProvider } from './secure-portal/session/Session.context';
import SupplierSurveyPage from './secure-portal/supplier/SupplierSurveyPage';
import { platformStackWorkflowPlugins } from './secure-portal/workflow/plugins-v2/plugins';
import UserProvider, { UserContext } from './user/User.context';
import { User } from './user/User.interface';
import UserEmailVerification from './user/UserEmailVerification';

import JiraLinkedEntities from './secure-portal/jira-server/JiraLinkedEntities';
import { flattenNavigation, QMSPortalPlugin } from './QMSPortalPlugin';
import Signup from './login/SignUp';
import OnboardingPage from './onboarding/OnboardingPage';
import CanSignupGuard from './guards/can-signup.guard';
import { defaultQmsQueryBuilderPlugins } from './queries/default';

// setup environment
muiApi.setEnvironment(environment);
platformStackApi.setEnvironment(environmentV2);

// NOTE: local development only (for Jira Connect integration)
if (env === 'development') {
  // override getAccessToken() method so they use the same token source
  muiApi.getAccessToken = api.getAccessToken;
  platformStackApi.getAccessToken = api.getAccessToken;
}

const sitePlugins = defaultSitePlugins
  .concat(QMSPortalPlugin)
  .concat(defaultQmsSitePlugins);

const siteEventPlugins = defaultSiteEventPlugins.concat(
  defaultQmsSiteEventPlugins
);

// default route permissions
const flatNav = flattenNavigation(navigationList);
const qmsRoutePermissions: SiteRoutePermission[] = flatNav.map(item => {
  return { path: item.path, permissions: item.roles || [] };
});

const JiraRouteHandler = () => {
  useJiraAcRoute();
  return <></>;
};

const RemoveJiraWidget = () => {
  useRemoveJiraWidget();
  return <> </>;
};

const AppRoot: React.FC = () => {
  // use QMS theme if location is not within editorPath
  const { pathname } = useLocation();
  const siteTheme = pathname.startsWith('/admin') ? undefined : theme;
  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <QueryParamProvider ReactRouterRoute={Route}>
        <JiraConnectProvider>
          <JiraServerProvider>
            <SiteProvider
              theme={siteTheme}
              plugins={sitePlugins}
              eventPlugins={siteEventPlugins}
              hostname={environment.siteDomain}
              formElementPlugins={defaultQmsFormElementPlugins}
              workflowPlugins={platformStackWorkflowPlugins}
              queryBuilderPlugins={defaultQmsQueryBuilderPlugins}
              // used to filter out navigation for hardcoded QMS routes
              routePermissions={qmsRoutePermissions}
              routeHasAccess={(user, permissionName) => {
                const roles = (user as User).roles || [];
                return roles.some(role => {
                  return [role.name, `ROLE-${role.id}`].includes(
                    permissionName
                  );
                });
              }}
            >
              <AppThemeProvider>
                <CssBaseline />
                <CheckVersion />
                {/* hide jira widget for specified route path */}
                <Route exact path='/forms/task' component={RemoveJiraWidget} />
                <UserProvider>
                  <SessionProvider>
                    <LoginProvider>
                      <EntityProvider>
                        <WorkflowProvider>
                          <FileExplorerProvider>
                            <div className='app'>
                              <JiraRouteHandler />
                              <SiteRootRoutes>
                                <Route exact path='/'>
                                  <UserContext.Consumer>
                                    {user =>
                                      user ? (
                                        <Redirect to='/portal' />
                                      ) : (
                                        <Redirect to='/login' />
                                      )
                                    }
                                  </UserContext.Consumer>
                                </Route>
                                <Route exact path='/login' component={Login} />
                                <ProtectedRoute
                                  guards={[CanSignupGuard]}
                                  exact
                                  path='/signup'
                                  component={Signup}
                                />
                                <ProtectedRoute
                                  guards={[SignupGuard]}
                                  exact
                                  path='/onboarding'
                                  component={OnboardingPage}
                                />
                                <ProtectedRoute
                                  guards={[GuestGuard]}
                                  path='/password/recover'
                                  component={PasswordRecover}
                                />
                                <Route
                                  exact
                                  path='/password/reset'
                                  component={PasswordReset}
                                />
                                <Route
                                  exact
                                  path='/email-verification'
                                  component={UserEmailVerification}
                                />
                                <Route
                                  exact
                                  path='/suppliers/survey'
                                  component={SupplierSurveyPage}
                                />
                                <ProtectedRoute
                                  guards={[AuthGuard]}
                                  path='/jira/linked-entities/:jiraKey'
                                  component={JiraLinkedEntities}
                                />
                              </SiteRootRoutes>
                            </div>
                          </FileExplorerProvider>
                        </WorkflowProvider>
                      </EntityProvider>
                    </LoginProvider>
                  </SessionProvider>
                </UserProvider>
              </AppThemeProvider>
            </SiteProvider>
          </JiraServerProvider>
        </JiraConnectProvider>
      </QueryParamProvider>
    </MuiPickersUtilsProvider>
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AppRoot />
    </BrowserRouter>
  );
};

export default App;
